// src/modules/app-views/products/pages/Document/tabs/AiInfoTab/TechnicalSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, theme } from 'antd';
import { ToolOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// HELPERS
// *******************************************************************************
// Safely render value that might be an object {uid, label} or a string
function renderValue(value) {
  if (!value) return null;
  if (typeof value === 'object' && value.label) return value.label;
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const TechnicalSection = ({ data, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const tc = data?.technicalCharacteristics;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = tc && (tc.material || tc.color || tc.size || tc.model || tc.voltage || tc.power || tc.capacity || tc.flavor || tc.scent || tc.poles || tc.current || tc.frequency || tc.standard || tc.installationType || tc.format || tc.application || tc.certification || tc.protectionDegree);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <Collapse
      className="collapsed-content-fit"
      collapsible="icon"
      defaultActiveKey={['technical']}
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'technical',
          label: (
            <Flex align="center" gap={8}>
              <ToolOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontWeight: 600 }}>{translateX('technical_characteristics')}</span>
            </Flex>
          ),
          showArrow: false,
          children: (
            <Descriptions
              bordered
              size="small"
              column={1}
              layout={isMobile ? 'vertical' : 'horizontal'}
              styles={{ label: { fontWeight: 500 } }}
            >
              {tc.material && (
                <Descriptions.Item label={translateX('material')}>
                  {renderValue(tc.material)}
                </Descriptions.Item>
              )}
              {tc.color && (
                <Descriptions.Item label={translateX('color')}>
                  {renderValue(tc.color)}
                </Descriptions.Item>
              )}
              {tc.size && (
                <Descriptions.Item label={translateX('size')}>
                  {renderValue(tc.size)}
                </Descriptions.Item>
              )}
              {tc.model && (
                <Descriptions.Item label={translateX('model')}>
                  {renderValue(tc.model)}
                </Descriptions.Item>
              )}
              {tc.voltage && (
                <Descriptions.Item label={translateX('voltage')}>
                  {renderValue(tc.voltage)}
                </Descriptions.Item>
              )}
              {tc.power && (
                <Descriptions.Item label={translateX('power')}>
                  {renderValue(tc.power)}
                </Descriptions.Item>
              )}
              {tc.capacity && (
                <Descriptions.Item label={translateX('capacity')}>
                  {renderValue(tc.capacity)}
                </Descriptions.Item>
              )}
              {tc.flavor && (
                <Descriptions.Item label={translateX('flavor')}>
                  {renderValue(tc.flavor)}
                </Descriptions.Item>
              )}
              {tc.scent && (
                <Descriptions.Item label={translateX('scent')}>
                  {renderValue(tc.scent)}
                </Descriptions.Item>
              )}
              {tc.poles && (
                <Descriptions.Item label={translateX('poles')}>
                  {renderValue(tc.poles)}
                </Descriptions.Item>
              )}
              {tc.current && (
                <Descriptions.Item label={translateX('current')}>
                  {renderValue(tc.current)}
                </Descriptions.Item>
              )}
              {tc.frequency && (
                <Descriptions.Item label={translateX('frequency')}>
                  {renderValue(tc.frequency)}
                </Descriptions.Item>
              )}
              {tc.standard && (
                <Descriptions.Item label={translateX('standard')}>
                  {renderValue(tc.standard)}
                </Descriptions.Item>
              )}
              {tc.installationType && (
                <Descriptions.Item label={translateX('installation_type')}>
                  {renderValue(tc.installationType)}
                </Descriptions.Item>
              )}
              {tc.format && (
                <Descriptions.Item label={translateX('format')}>
                  {renderValue(tc.format)}
                </Descriptions.Item>
              )}
              {tc.application && (
                <Descriptions.Item label={translateX('application')}>
                  {renderValue(tc.application)}
                </Descriptions.Item>
              )}
              {tc.certification && (
                <Descriptions.Item label={translateX('certification')}>
                  {renderValue(tc.certification)}
                </Descriptions.Item>
              )}
              {tc.protectionDegree && (
                <Descriptions.Item label={translateX('protection_degree')}>
                  {renderValue(tc.protectionDegree)}
                </Descriptions.Item>
              )}
            </Descriptions>
          ),
        },
      ]}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default TechnicalSection;
